import React, {useEffect, useMemo, useState} from "react"
import {BackButton} from "../../components/BackButton/BackButton"
import {useHistory, useLocation} from "react-router-dom"
import {useFormik} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import * as yup from "yup"
import {Helmet} from "react-helmet-async"
import type {UploadProps} from "antd"
import {Button, Checkbox, Form, Modal, Tag, Upload} from "antd"
import {FormInput} from "../../components/Form/FormInput"
import {changeAttributesVersion, createAttributesVersion, getAttributesVersions, setAttributesVersions} from "../../store/attributesVersionReducer"
import {JsonAttributes} from "./JsonAttributes"
import {InboxOutlined} from "@ant-design/icons"
import {AttributesVersionResponse} from "../../models/response"
import {UpdateFileType} from "../../models/request/UpdateFileType"
import {s3Url} from "../../api/API"
import {catchError} from "../../store/sliceHelper"
import {FormTextarea} from "../../components/Form/FormTextarea"

const {Dragger} = Upload

const schema = yup.object().shape({
    version: yup.number().required("Введите номер версии").min(1, "Минимум 1"),
    name: yup.string().required("Введите название").max(100, "Максимальная длина 100 символов"),
    comment: yup.string().max(2000, "Максимальная длина 2000 символов"),
    changelog: yup.string().max(1000, "Максимальная длина 1000 символов"),
    attributes: yup.string().required("Введите атрибуты"),
})

export const AttributesVersionForm = () =>
{
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation<{ version: AttributesVersionResponse }>()
    const version = location?.state?.version
    const status = useSelector((state: StateType) => state.appReducer.status)
    const versions = useSelector((state: StateType) => state.attributesVersionReducer.versions)
    const [submitCallback, setSubmitCallback] = useState<any>(null)
    const controller = useMemo(() => new AbortController(), [])
    const [firmware, setFirmware] = useState<any>(null)
    const [lastVersionAttr, setLastVersionAttr] = useState<string | null>(null)
    const [updateFileType, setUpdateFileType] = useState<UpdateFileType>(UpdateFileType.NoChange)

    useEffect(() =>
    {
        dispatch(getAttributesVersions({controller}))

        return () =>
        {
            controller.abort()
            dispatch(setAttributesVersions(null))
        }
    }, [dispatch, controller])

    useEffect(() =>
    {
        if (versions)
        {
            const versionsActive = versions.data.filter(i => i.active)
            setLastVersionAttr(versionsActive[0].attributes)
        }
    }, [versions])

    useEffect(() =>
    {
        (async () =>
        {
            if (version?.firmware)
            {
                try
                {
                    const arr = version.firmware.split("/")
                    const name = arr[arr.length - 1]
                    const res = await fetch(`${s3Url}/${version?.firmware}`)
                    const blob = await res.blob()
                    const file = new File([blob], name, {type: "application/octet-stream"})
                    setFirmware(file)
                    setUpdateFileType(UpdateFileType.NoChange)
                } catch (e)
                {
                    catchError(e, dispatch)
                }
            }
        })()
    }, [version])

    const formik = useFormik({
        initialValues: {
            version: version?.version || "",
            name: version?.name || "",
            comment: version?.comment || "",
            changelog: version?.changelog || "",
            attributes: version?.attributes || "",
            active: version ? version.active : false,
            updateFileType,
        },
        validationSchema: schema,
        onSubmit: async (values) =>
        {
            if (submitCallback && await submitCallback())
            {
                const request = {
                    version: values.version as number,
                    name: values.name,
                    comment: values.comment,
                    changelog: values.changelog,
                    attributes: values.attributes,
                    active: values.active,
                    firmware,
                    updateFileType,
                }

                let res: any
                if (version)
                {
                    res = await dispatch(changeAttributesVersion({
                        id: version.version,
                        ...request,
                        controller,
                    }))
                }
                else
                {
                    res = await dispatch(createAttributesVersion({
                        ...request,
                        controller,
                    }))
                }
                if (res?.payload?.status === 0) {
                    if (!version)
                    {
                        history.replace(`/attributesVersion/${res.payload.data.version}`)
                    }
                    else
                    {
                        history.goBack()
                    }
                }
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue} = formik

    const uploadProps: UploadProps = useMemo(() => ({
        name: "file",
        multiple: false,
        accept: ".bin",
        maxCount: 1,
        fileList: firmware ? [firmware] : [],
        customRequest: () => true,
        onChange(info)
        {
            setFirmware(info.file.originFileObj)
            if (info.fileList.length > 0) setUpdateFileType(UpdateFileType.New)
            else setUpdateFileType(UpdateFileType.Remove)
        },
        onRemove: () =>
        {
            setTimeout(() => setFirmware(null), 10)
            setUpdateFileType(UpdateFileType.Remove)
            return true
        },
    }), [firmware])

    const info = () =>
    {
        Modal.info({
            title: "Внимание!",
            content: (
                <div>
                    <p>После активации версии нельзя будет изменить файл прошивки.</p>
                </div>
            ),
            onOk()
            {
            },
            maskClosable: true,
        })
    }

    useEffect(() =>
    {
        if (values.active && versions)
        {
            if (version)
            {
                const lastActive = versions.data.filter(i => i.active)[0]
                if (version.version > lastActive.version) info()
            }
            else
            {
                info()
            }
        }
    }, [values.active, version, versions])

    useEffect(() =>
    {
        if (!version && versions)
        {
            if (versions)
            {
                const lastVersion = versions.data[0]
                setFieldValue("version", lastVersion.version + 1)
                setFieldValue("name", `Версия ${lastVersion.version + 1}`)
            }
        }
    }, [version, versions])

    return (
        <div className="form-page">
            <Helmet>
                <title>{version ? "Обновление версии" : "Новая версия"}</title>
            </Helmet>

            <Form className="form-container"
                  style={{width: "auto", alignSelf: "auto"}}
                  autoComplete="off"
                  onSubmitCapture={handleSubmit}
            >
                <div className="description-header"><BackButton onClick={() => history.goBack()}/></div>

                <div className="description-title">{version ? "Обновление версии" : "Новая версия"}</div>

                <div style={{width: 400, margin: "auto"}}>
                    <FormInput placeholder="Номер версии"
                               value={values.version}
                               name="version"
                               onBlur={handleBlur}
                               onChange={handleChange}
                               isInvalid={touched.version && errors.version}
                               allowClear
                               disabled={!!version}
                    />

                    <FormInput placeholder="Название"
                               value={values.name}
                               name="name"
                               onBlur={handleBlur}
                               onChange={handleChange}
                               isInvalid={touched.name && errors.name}
                               allowClear
                    />

                    <FormInput placeholder="Комментарий"
                               value={values.comment}
                               name="comment"
                               onBlur={handleBlur}
                               onChange={handleChange}
                               isInvalid={touched.comment && errors.comment}
                               allowClear
                    />

                    <FormTextarea placeholder="Список изменений"
                                  value={values.changelog}
                                  name="changelog"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  isInvalid={touched.changelog && errors.changelog}
                                  allowClear
                                  autoSize={{minRows: 2}}
                    />
                </div>

                <JsonAttributes attributes={version?.attributes || lastVersionAttr}
                                setAttributes={(value) => setFieldValue("attributes", value)}
                                setSubmitCallback={setSubmitCallback}
                                versions={versions}
                />

                <div style={{width: 400, margin: "auto"}}>
                    {
                        !version?.active
                            ? <>
                                <Dragger style={{marginTop: 30}} {...uploadProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined/>
                                    </p>
                                    <p className="ant-upload-hint" style={{marginLeft: 20, marginRight: 20}}>Нажмите или перетащите файл в эту область, чтобы загрузить прошивку</p>
                                </Dragger>

                                <div style={{marginTop: 30}}>
                                    <Checkbox checked={values.active}
                                              name="active"
                                              onChange={handleChange}
                                              disabled={!firmware}
                                    >
                                        <Tag color={values.active ? "green" : "default"}>Активно</Tag>
                                    </Checkbox>
                                </div>
                            </>
                            : null
                    }

                    <div className="form-button-block">
                        <Button type="primary"
                                htmlType="submit"
                                loading={status === "loading"}
                        >
                            Сохранить
                        </Button>
                        <Button type="default"
                                onClick={() => history.goBack()}
                        >
                            Отмена
                        </Button>
                    </div>
                </div>
            </Form>
        </div>
    )
}

